/**
 * AnalyticsDashboard — tenant KPIs and reports.
 * Route: /analytics
 */
import { useEffect, useState } from "react";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";

type DashboardData = {
  as_of: string;
  customers: {
    total: number;
    this_month: number;
    dmrv_verified: number;
    dmrv_in_progress: number;
    dmrv_not_started: number;
    verification_rate_pct: number;
  };
  revenue: { this_month: number; this_year: number; total: number; currency: string };
  arrears: { total_amount: number; contracts_in_arrears: number };
  devices: { total: number; active: number; unlocked: number };
  installations: { pending: number; verified: number };
};

type SalesAnalysis = {
  period: string;
  total_transactions: number;
  total_revenue: number;
  by_payment_method: Record<string, number>;
  monthly_trend: Record<string, number>;
};

type Geographic = {
  total_customers: number;
  by_region: { region: string; count: number; pct: number }[];
  by_district: { district: string; count: number }[];
};

type ArrearsReport = {
  total_active_contracts: number;
  contracts_in_arrears: number;
  delinquency_rate_pct: number;
  total_arrears_amount: number;
  currency: string;
  contracts: {
    contract_id: number;
    contract_number: string;
    customer_id: number;
    arrears: number;
    arrears_months: number;
    amount_remaining: number;
  }[];
};

type Tab = "overview" | "sales" | "geographic" | "arrears";
type SectionState = "idle" | "loading" | "error" | "success";

function StatCard({ label, value, sub, tone }: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: "success" | "warning" | "error" | "info" | "neutral";
}) {
  return (
    <AppCard>
      <div className="pg-stat-label">{label}</div>
      <div className="pg-stat-value">{value}</div>
      {sub && <div className="pg-text-muted pg-stat-sub">{sub}</div>}
      {tone && <div className="pg-stat-indicator" data-tone={tone} />}
    </AppCard>
  );
}

function money(n: number, currency = "TZS") {
  return `${currency} ${Math.round(n).toLocaleString()}`;
}

export function AnalyticsDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [fetchStatus, setFetchStatus] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [tab, setTab] = useState<Tab>("overview");

  const [sales, setSales] = useState<SalesAnalysis | null>(null);
  const [salesState, setSalesState] = useState<SectionState>("idle");
  const [salesError, setSalesError] = useState("");

  const [geo, setGeo] = useState<Geographic | null>(null);
  const [geoState, setGeoState] = useState<SectionState>("idle");
  const [geoError, setGeoError] = useState("");

  const [arrears, setArrears] = useState<ArrearsReport | null>(null);
  const [arrearsState, setArrearsState] = useState<SectionState>("idle");
  const [arrearsError, setArrearsError] = useState("");

  const loadOverview = async () => {
    setFetchStatus("loading");
    try {
      const d = await api.get<DashboardData>("/evaluator/dashboard");
      setData(d);
      setFetchStatus("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load analytics dashboard");
      setFetchStatus("error");
    }
  };
  useEffect(() => { loadOverview(); }, []);

  const loadSales = async () => {
    setSalesState("loading");
    try {
      const d = await api.get<SalesAnalysis>("/evaluator/sales-analysis");
      setSales(d);
      setSalesState("success");
    } catch (ex: unknown) {
      setSalesError(ex instanceof Error ? ex.message : "Failed to load sales analysis");
      setSalesState("error");
    }
  };

  const loadGeo = async () => {
    setGeoState("loading");
    try {
      const d = await api.get<Geographic>("/evaluator/geographic");
      setGeo(d);
      setGeoState("success");
    } catch (ex: unknown) {
      setGeoError(ex instanceof Error ? ex.message : "Failed to load geographic distribution");
      setGeoState("error");
    }
  };

  const loadArrears = async () => {
    setArrearsState("loading");
    try {
      const d = await api.get<ArrearsReport>("/evaluator/arrears");
      setArrears(d);
      setArrearsState("success");
    } catch (ex: unknown) {
      setArrearsError(ex instanceof Error ? ex.message : "Failed to load arrears report");
      setArrearsState("error");
    }
  };

  const selectTab = (t: Tab) => {
    setTab(t);
    if (t === "sales" && salesState === "idle") loadSales();
    if (t === "geographic" && geoState === "idle") loadGeo();
    if (t === "arrears" && arrearsState === "idle") loadArrears();
  };

  const sectionStatus = (state: SectionState): "loading" | "error" | "success" => {
    if (state === "loading") return "loading";
    if (state === "error") return "error";
    return "success";
  };

  if (fetchStatus === "loading" || fetchStatus === "error") {
    return (
      <div>
        <PageHeader title="Analytics" subtitle="Tenant KPIs from completed payments and active records." />
        <PageState status={fetchStatus} error={pageError} onRetry={loadOverview} />
      </div>
    );
  }

  const d = data!;

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Tenant KPIs from completed payments and active records."
        actions={
          <AppButton type="button" variant="secondary" onClick={loadOverview}>Refresh</AppButton>
        }
      />

      <div className="pg-tabs">
        <button type="button" className={`pg-tab${tab === "overview" ? " active" : ""}`} onClick={() => selectTab("overview")}>Overview</button>
        <button type="button" className={`pg-tab${tab === "sales" ? " active" : ""}`} onClick={() => selectTab("sales")}>Sales Analysis</button>
        <button type="button" className={`pg-tab${tab === "geographic" ? " active" : ""}`} onClick={() => selectTab("geographic")}>Geographic</button>
        <button type="button" className={`pg-tab${tab === "arrears" ? " active" : ""}`} onClick={() => selectTab("arrears")}>Arrears</button>
      </div>

      {tab === "overview" && (
        <>
          <div className="pg-stats-row">
            <StatCard label="Total Customers" value={d.customers.total.toLocaleString()} sub={`+${d.customers.this_month} this month`} tone="info" />
            <StatCard label="Revenue This Month" value={money(d.revenue.this_month, d.revenue.currency)} sub={`${money(d.revenue.this_year, d.revenue.currency)} this year`} tone="success" />
            <StatCard label="Total Revenue" value={money(d.revenue.total, d.revenue.currency)} sub="all-time, completed payments" tone="success" />
            <StatCard label="Arrears" value={money(d.arrears.total_amount, d.revenue.currency)} sub={`${d.arrears.contracts_in_arrears} contract${d.arrears.contracts_in_arrears !== 1 ? "s" : ""} behind`} tone={d.arrears.contracts_in_arrears > 0 ? "warning" : "neutral"} />
            <StatCard label="Active Devices" value={d.devices.active.toLocaleString()} sub={`${d.devices.total} total fleet`} tone="success" />
            <StatCard label="Unlocked Devices" value={d.devices.unlocked.toLocaleString()} sub="awaiting activation" tone="neutral" />
            <StatCard label="Installations Pending" value={d.installations.pending.toLocaleString()} sub={`${d.installations.verified} verified`} tone={d.installations.pending > 0 ? "warning" : "success"} />
            <StatCard label="dMRV Verification Rate" value={`${d.customers.verification_rate_pct}%`} sub={`${d.customers.dmrv_verified} of ${d.customers.total} verified`} tone="info" />
          </div>

          <AppCard title="dMRV Breakdown">
            <table className="pg-table">
              <thead>
                <tr><th>Status</th><th style={{ textAlign: "right" }}>Customers</th></tr>
              </thead>
              <tbody>
                <tr><td><AppBadge tone="success">Verified</AppBadge></td><td style={{ textAlign: "right" }}>{d.customers.dmrv_verified}</td></tr>
                <tr><td><AppBadge tone="info">In Progress</AppBadge></td><td style={{ textAlign: "right" }}>{d.customers.dmrv_in_progress}</td></tr>
                <tr><td><AppBadge tone="neutral">Not Started</AppBadge></td><td style={{ textAlign: "right" }}>{d.customers.dmrv_not_started}</td></tr>
              </tbody>
            </table>
          </AppCard>
        </>
      )}

      {tab === "sales" && (
        <AppCard title="Sales Analysis — this month">
          <PageState status={sectionStatus(salesState)} error={salesError} onRetry={loadSales}>
            {sales && (
              <>
                <div className="pg-stats-row">
                  <StatCard label="Transactions" value={sales.total_transactions} tone="info" />
                  <StatCard label="Revenue" value={money(sales.total_revenue)} tone="success" />
                </div>
                <h3 style={{ marginTop: 0 }}>Revenue by Payment Method</h3>
                <table className="pg-table">
                  <thead><tr><th>Method</th><th style={{ textAlign: "right" }}>Total (TZS)</th></tr></thead>
                  <tbody>
                    {Object.entries(sales.by_payment_method).length === 0 && (
                      <tr><td colSpan={2}>No completed payments this period.</td></tr>
                    )}
                    {Object.entries(sales.by_payment_method).map(([method, total]) => (
                      <tr key={method}>
                        <td>{method.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</td>
                        <td style={{ textAlign: "right" }}>{Math.round(total).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h3>Monthly Trend (last 12 months)</h3>
                <table className="pg-table">
                  <thead><tr><th>Month</th><th style={{ textAlign: "right" }}>Revenue (TZS)</th></tr></thead>
                  <tbody>
                    {Object.entries(sales.monthly_trend).map(([month, total]) => (
                      <tr key={month}>
                        <td>{month}</td>
                        <td style={{ textAlign: "right" }}>{Math.round(total).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </PageState>
        </AppCard>
      )}

      {tab === "geographic" && (
        <AppCard title="Geographic Distribution">
          <PageState status={sectionStatus(geoState)} error={geoError} onRetry={loadGeo}>
            {geo && (
              <div className="pg-two-col">
                <div>
                  <h3 style={{ marginTop: 0 }}>By Region</h3>
                  <table className="pg-table">
                    <thead><tr><th>Region</th><th style={{ textAlign: "right" }}>Customers</th><th style={{ textAlign: "right" }}>Share</th></tr></thead>
                    <tbody>
                      {geo.by_region.length === 0 && <tr><td colSpan={3}>No active customers with a region recorded.</td></tr>}
                      {geo.by_region.map((r) => (
                        <tr key={r.region}><td>{r.region}</td><td style={{ textAlign: "right" }}>{r.count}</td><td style={{ textAlign: "right" }}>{r.pct}%</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h3 style={{ marginTop: 0 }}>Top Districts</h3>
                  <table className="pg-table">
                    <thead><tr><th>Region / District</th><th style={{ textAlign: "right" }}>Customers</th></tr></thead>
                    <tbody>
                      {geo.by_district.length === 0 && <tr><td colSpan={2}>No data.</td></tr>}
                      {geo.by_district.map((r) => (
                        <tr key={r.district}><td>{r.district}</td><td style={{ textAlign: "right" }}>{r.count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </PageState>
        </AppCard>
      )}

      {tab === "arrears" && (
        <AppCard title="Arrears Report">
          <PageState status={sectionStatus(arrearsState)} error={arrearsError} onRetry={loadArrears}>
            {arrears && (
              <>
                <div className="pg-stats-row">
                  <StatCard label="Active Contracts" value={arrears.total_active_contracts} tone="info" />
                  <StatCard label="In Arrears" value={arrears.contracts_in_arrears} tone={arrears.contracts_in_arrears > 0 ? "warning" : "success"} />
                  <StatCard label="Delinquency Rate" value={`${arrears.delinquency_rate_pct}%`} tone={arrears.delinquency_rate_pct > 0 ? "warning" : "success"} />
                  <StatCard label="Total Arrears" value={money(arrears.total_arrears_amount, arrears.currency)} tone="warning" />
                </div>
                <table className="pg-table">
                  <thead>
                    <tr>
                      <th>Contract #</th>
                      <th>Customer ID</th>
                      <th style={{ textAlign: "right" }}>Arrears (TZS)</th>
                      <th style={{ textAlign: "right" }}>Months Behind</th>
                      <th style={{ textAlign: "right" }}>Remaining (TZS)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {arrears.contracts.length === 0 && <tr><td colSpan={5}>No contracts currently in arrears.</td></tr>}
                    {arrears.contracts.map((c) => (
                      <tr key={c.contract_id}>
                        <td>{c.contract_number}</td>
                        <td>{c.customer_id}</td>
                        <td style={{ textAlign: "right" }}>{Math.round(c.arrears).toLocaleString()}</td>
                        <td style={{ textAlign: "right" }}>{c.arrears_months}</td>
                        <td style={{ textAlign: "right" }}>{Math.round(c.amount_remaining).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {arrears.contracts.length >= 100 && (
                  <p className="pg-text-muted" style={{ marginTop: 8 }}>Showing the first 100 contracts in arrears.</p>
                )}
              </>
            )}
          </PageState>
        </AppCard>
      )}
    </div>
  );
}
